import { useCallback, useEffect, useRef, useState } from 'react';
import { AppState, type AppStateStatus } from 'react-native';

// ─────────────────────────────────────────────────────────────────────

/**
 * Returns the current app state.
 *
 * Supports pausing the observer and calling a callback when the app becomes visible.
 *
 * Also works on the web platform by using the `visibilitychange` event to
 * detect when the browser tab becomes visible.
 *
 * @see {@link https://reactnative.dev/docs/appstate}
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/Page_Visibility_API}
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/Document/visibilitychange_event}
 */
export const useAppState = (params: Params = {}) => {
  const { onVisible, pause } = params;

  // ─── Refs ────────────────────────────────────────────────────────────

  const appStateRef = useRef(AppState.currentState);

  // ─── States ──────────────────────────────────────────────────────────

  // NOTE: React Native types `currentState` as a nullable string, but it holds
  //       an `AppStateStatus` value at runtime.
  const [appState, setAppState] = useState(AppState.currentState as AppStateStatus);

  // ─── Helpers ─────────────────────────────────────────────────────────

  const onAppStateChange = useCallback(
    (state: AppStateStatus) => {
      const wasActive = appStateRef.current === 'active';
      const isActive = state === 'active';
      const becomeVisible = !wasActive && isActive;

      setAppState(state);
      appStateRef.current = state;

      if (becomeVisible) {
        onVisible?.();
      }
    },
    [onVisible],
  );

  const startObserver = useCallback(() => {
    if (pause) return;

    const subscription = AppState.addEventListener('change', onAppStateChange);

    return () => {
      subscription.remove();
    };
  }, [onAppStateChange, pause]);

  // ─── Effects ─────────────────────────────────────────────────────────

  useEffect(startObserver, [startObserver]);

  // ─────────────────────────────────────────────────────────────────────

  return appState;
};

// ─── Types ───────────────────────────────────────────────────────────

type Params = {
  pause?: boolean;
  onVisible?: () => void;
};
